import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncated-text";
import { format, parseISO } from "date-fns";
import {
  useRunLandscapeScheduleNow,
  useDeleteLandscapeSchedule,
} from "@/resources/landscape-schedule";
import type { UpcomingLandscapeSchedule } from "@/types";
import { EditScheduleTimingModal } from "./EditScheduleTimingModal";

function formatFrequencyLabel(
  scheduleType: "one_time" | "recurring",
  frequency: string | null
): string {
  if (scheduleType === "one_time") return "One-time";
  if (!frequency) return "Recurring";
  return frequency.charAt(0).toUpperCase() + frequency.slice(1);
}

interface ManageSchedulePopoverProps {
  schedule: UpcomingLandscapeSchedule;
}

export function ManageSchedulePopover({ schedule }: ManageSchedulePopoverProps) {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [runConfirm, setRunConfirm] = useState(false);

  const { mutate: runNow, isPending: isRunning } = useRunLandscapeScheduleNow();
  const { mutate: deleteSchedule, isPending: isDeleting } = useDeleteLandscapeSchedule();

  const handleRunNow = () => {
    runNow(
      { id: schedule.id },
      {
        onSuccess: (data) => {
          toast.success("Landscape refresh started", {
            action: {
              label: "View Results",
              onClick: () => {
                window.location.href = `/landscapes/${data.landscape_id}`;
              },
            },
          });
          setOpen(false);
        },
        onError: () => {
          toast.error("Failed to start landscape refresh");
          setRunConfirm(false);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteSchedule(
      { id: schedule.id },
      {
        onSuccess: () => {
          toast.success("Schedule deleted");
          setOpen(false);
        },
        onError: () => {
          toast.error("Failed to delete schedule");
        },
      }
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setRunConfirm(false); }}>
        <PopoverTrigger asChild>
          <button className="text-xs text-primary hover:text-primary/80 font-medium shrink-0 transition-colors">
            Manage
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="end">
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-foreground">
                <TruncatedText>{schedule.landscapePhrase}</TruncatedText>
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-[10px] text-muted-foreground">Next run</span>
                <span className="text-[10px] text-foreground">
                  {schedule.nextRunAt
                    ? format(parseISO(schedule.nextRunAt), "MMM d, yyyy h:mm a")
                    : "TBD"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[10px] text-muted-foreground">Frequency</span>
                <span className="text-[10px] text-foreground">
                  {formatFrequencyLabel(schedule.scheduleType, schedule.frequency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[10px] text-muted-foreground">Timezone</span>
                <TruncatedText className="text-[10px] text-foreground max-w-[120px]">
                  {schedule.timezone}
                </TruncatedText>
              </div>
            </div>

            <div className="border-t border-border" />

            <div className="space-y-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs h-7 px-2"
                onClick={() => { setEditOpen(true); setOpen(false); }}
              >
                Edit Timing
              </Button>

              {!runConfirm ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs h-7 px-2"
                  onClick={() => setRunConfirm(true)}
                >
                  Run Now
                </Button>
              ) : (
                <div className="space-y-1.5 rounded-md bg-muted/50 p-2">
                  <p className="text-[10px] text-muted-foreground">
                    This will immediately generate a new landscape.
                  </p>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      className="h-6 text-[10px] px-2 flex-1"
                      onClick={handleRunNow}
                      disabled={isRunning}
                    >
                      {isRunning ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        "Confirm"
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] px-2 flex-1"
                      onClick={() => setRunConfirm(false)}
                      disabled={isRunning}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {schedule.lastRunLandscapeId != null && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs h-7 px-2 gap-1.5"
                  asChild
                >
                  <Link to={`/landscapes/${schedule.lastRunLandscapeId}`}>
                    View Last Run
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </Button>
              )}
            </div>

            <div className="border-t border-border" />

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-xs h-7 px-2 text-destructive hover:text-destructive"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                  ) : null}
                  Delete Schedule
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Schedule?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove this scheduled refresh. This action cannot be
                    undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </PopoverContent>
      </Popover>

      <EditScheduleTimingModal
        open={editOpen}
        onOpenChange={setEditOpen}
        schedule={schedule}
      />
    </>
  );
}
